import React, { useCallback, useMemo, useState } from 'react';

import { ClickAwayListener } from '@material-ui/core';
import { useInView } from 'react-intersection-observer';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import { updateCompetencyDescription } from 'services/competencies';
import { showErrorSnackbar } from 'services/snackbar';

import { Typography2 } from 'uiToolkit/Typography';

import { DescriptionCellBulletPoints, ParagraphDescriptionDataCell } from './DescriptionCellComponents';
import DimensionContentType from './DimensionContentType';
import DisableCompetencyDescription from './DisabledCompetencyDescription';

const DescriptionContent = ({
  isCompetencyDisabled,
  isCompetencyBulletPoints,
  descriptionBullets = [],
  descriptionParagraph,
  handleChange,
  isVisible,
  setIsEditing,
}) => {
  if (!isVisible) return null;
  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {isCompetencyDisabled ? (
        <Typography2 style={{ fontStyle: 'italic' }}>This description cell is disabled!</Typography2>
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          {isCompetencyBulletPoints ? (
            <DescriptionCellBulletPoints
              listPoints={descriptionBullets ?? []}
              onChange={handleChange}
              setIsEditing={setIsEditing}
              isDisabled={isCompetencyDisabled}
            />
          ) : (
            <ParagraphDescriptionDataCell
              paragraphText={descriptionParagraph}
              onChange={handleChange}
              setIsEditing={setIsEditing}
            />
          )}
        </>
      )}
    </>
  );
};

const DescriptionControls = ({
  functionId,
  isCompetencyDisabled,
  handleDescriptionDisable,
  isCompetencyBulletPoints,
  handleDescriptionTypeChange,
}) => {
  // using is inView hook to only render
  // the controls when cell is view else it's causing performance issue
  // as buttons take time to render in large dataset of table
  const { ref, inView } = useInView({ triggerOnce: true });

  return (
    <ControlsContainer ref={ref}>
      <DisableCompetencyDescription
        isDisabled={isCompetencyDisabled}
        onClickHandler={handleDescriptionDisable}
        isVisible={functionId && inView}
      />

      <DimensionContentType
        contentType={isCompetencyBulletPoints ? 'BULLETS' : 'PARAGRAPH'}
        onChange={handleDescriptionTypeChange}
        isVisible={!isCompetencyDisabled && functionId && inView}
      />
    </ControlsContainer>
  );
};

const ControlsContainer = styled.div``;

const DescriptionCell = ({ dataObj, fetchCompetency, competencyId, functionId, roleId, setAddNew = () => {} }) => {
  const [description, setDescription] = useState(dataObj);
  const [isEditing, setIsEditing] = useState(false);

  const { ref, inView } = useInView({ triggerOnce: true });

  const dispatch = useDispatch();
  const handleChange = (updatedObj) => {
    setDescription((prev) => ({ ...prev, ...updatedObj }));
  };

  const handleClickAway = useCallback(() => {
    if (competencyId) {
      const propertyList = ['isBullet', 'bullets', 'description'];
      // if (haveSamePropValues(dataObj, description, propertyList)) {
      //   setIsEditing(false);
      // } else {
      if (isEditing) {
        updateDescription({
          ...description,
          jobRoleId: roleId,
          jobFunctionId: functionId,
          organizationTagId: +competencyId,
        });
        setIsEditing(false);
        setAddNew(false);
      }
    }
    // }
  }, [description, dataObj, competencyId]);

  const updateDescription = (description) => {
    updateCompetencyDescription({
      description,
      competencyId,
      functionId: String(functionId),
    })
      .then((orgTags) => {
        fetchCompetency();
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message));
      });
  };

  const handleDescriptionTypeChange = (type) => {
    if (type === 'BULLETS') {
      setDescription((prev) => ({ ...prev, isBullet: true }));
      updateDescription({ ...description, isBullet: true });
    } else {
      setDescription((prev) => ({ ...prev, isBullet: false }));
      updateDescription({ ...description, isBullet: false });
    }
  };

  const handleDescriptionDisable = useCallback(() => {
    const { isDisabled } = description;
    setDescription((prev) => ({ ...prev, isDisabled: !isDisabled }));
    updateDescription({ ...description, isDisabled: !isDisabled });
  }, []);

  const isCompetencyDisabled = useMemo(() => description?.isDisabled, [description]);
  const isCompetencyBulletPoints = useMemo(() => description?.isBullet, [description]);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <DescriptionCellContainer ref={ref}>
        <StyledDimensionColumnCell
          style={{
            flex: '1 1 0%',
            pointerEvents: isCompetencyDisabled ? 'none' : 'all',
            borderBottom: 'none',
          }}
          $isDisabled={isCompetencyDisabled}
        >
          <DescriptionContent
            isCompetencyDisabled={isCompetencyDisabled}
            isCompetencyBulletPoints={isCompetencyBulletPoints}
            descriptionBullets={description?.bullets}
            descriptionParagraph={description?.description}
            handleChange={handleChange}
            setIsEditing={setIsEditing}
            isVisible={inView}
          />
        </StyledDimensionColumnCell>
        <DescriptionControls
          functionId={functionId}
          isCompetencyDisabled={isCompetencyDisabled}
          isCompetencyBulletPoints={isCompetencyBulletPoints}
          handleDescriptionDisable={handleDescriptionDisable}
          handleDescriptionTypeChange={handleDescriptionTypeChange}
        />
      </DescriptionCellContainer>
    </ClickAwayListener>
  );
};

const DescriptionCellContainer = styled('div')`
  display: flex;
  flex-direction: column;
`;

const StyledDimensionColumnCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: stretch;

  width: 280px;
  min-height: 240px;

  padding: 1.5rem;
  border: ${(props) => `1px solid ${props.theme.palette.borders.lighterGrey}`};
  border-left-width: 0px;

  background-color: white;

  ${(props) =>
    props.fixed &&
    css`
      width: 240px;
      position: sticky;
      left: 0px;
      z-index: 1;
      border-left-width: 1px;
    `};
  ${(props) =>
    props.$isDisabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}
`;

export default DescriptionCell;
